import { useState, useEffect, useCallback, useRef } from 'react'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import Link from '@mui/material/Link'
import IconButton from '@mui/material/IconButton'
import FilterCenterFocusIcon from '@mui/icons-material/FilterCenterFocus'
import AllOutIcon from '@mui/icons-material/AllOut'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import api from 'services/api'
import useToast from 'hooks/useToast'
import Pulse from 'components/core/Pulse'
import BubbleField from './BubbleField'
import SessionAverages from './SessionAverages'
import TimezoneMap from './TimezoneMap'
import CohortSankey from './CohortSankey'
import Timeline from './Timeline'
import DayBlocks from './DayBlocks'
import MicTimeBucket from './MicTimeBucket'
import CumulativeTimeline from './CumulativeTimeline'
import RangePicker, { resolveRange } from './RangePicker'
import {
  GROUP_VALUES,
  GROUP_COLORS,
  GROUP_TITLES,
  VALUE_LABELS,
  PERSONAS,
} from './cohorts'

const POLL_INTERVAL_MS = 30_000
// The trailing-week window (typical-day line, 7-day total) moves slowly
// and is a heavier query, so it refreshes on its own relaxed cadence.
const WEEK_POLL_INTERVAL_MS = 5 * 60_000
// Clip sparkles replay on a fixed tape delay so the real inter-clip
// timing is preserved (a poll only reveals clips in 30s batches, up to
// 15s stale from the server cache — 45s covers the worst case, so a
// clip's scheduled time is always still in the future when we learn of
// it). Never a random stagger: spacing is real, just uniformly shifted.
const CLIP_SPARKLE_DELAY_MS = 45_000
// A clip learned about long after capture (poll gap, laptop asleep) is
// seeded silently instead of firing a stale burst of sparkles.
const CLIP_SPARKLE_MAX_OVERDUE_MS = 5 * 60_000
// Temporary: a second sparkle (carrying the quote/title text) when a
// known clip gains its transcription-derived text a minute or two after
// capture. Flip off to keep only the create-time sparkle.
const QUOTE_SPARKLE_ENABLED = true
// Dev aid: flip on to show a header button that fires a fake clip event
// (create sparkle, then a quote 2s later) for previewing the animations.
const SPARKLE_TEST_BUTTON = false

// Only the pipeline's best_quote reads as speech; titles are labels,
// so a clip with no quote just sparkles without a callout.
const clipText = (clip) => clip.best_quote || null

const clipPersonaKey = (clip) => {
  const persona = PERSONAS.find(({ match }) => match(clip))
  return persona ? persona.key : null
}
// Wallboard mode freezes the header + top section at the layout they
// have at the page's 1600px max width (1600 minus the px:6 gutters) and
// scales that design uniformly, so every element keeps its proportions.
// Scaling uses transform, not CSS zoom: iOS Safari counter-boosts font
// sizes inside a zoomed element (ignoring text-size-adjust), which fed
// back through the fit computation and collapsed the layout on iPad.
// transform happens at paint time, after layout, so fonts are left alone.
const WALLBOARD_DESIGN_WIDTH = 1504

const sumBy = (cohorts, dimension, value) =>
  cohorts
    .filter((cohort) => cohort[dimension] === value)
    .reduce((total, cohort) => total + cohort.count, 0)

const Breakdown = ({ cohorts }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {Object.entries(GROUP_VALUES).map(([dimension, values]) => (
      <Box key={dimension}>
        <Typography variant="caption" color="text.secondary">
          {GROUP_TITLES[dimension]}
        </Typography>
        {values.map((value) => (
          <Box
            key={value}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: GROUP_COLORS[value],
              }}
            />
            <Typography variant="body2">
              {VALUE_LABELS[value]}: <b>{sumBy(cohorts, dimension, value)}</b>
            </Typography>
          </Box>
        ))}
      </Box>
    ))}
  </Box>
)

const Heartbeats = () => {
  const toast = useToast()
  const [live, setLive] = useState(null)
  const [history, setHistory] = useState(null)
  const [historyError, setHistoryError] = useState(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [range, setRange] = useState({ key: 'today' })
  const [loading, setLoading] = useState(true)
  const [gathered, setGathered] = useState(true)
  // The day-blocks panel is pinned to today regardless of the range
  // picker, so it carries its own fetch.
  const [day, setDay] = useState(null)
  const [dayError, setDayError] = useState(null)
  const [week, setWeek] = useState(null)
  const [fullscreen, setFullscreen] = useState(false)

  const rangeRef = useRef(range)
  rangeRef.current = range
  const wallboardRef = useRef(null)
  const worldMapRef = useRef(null)

  // Sparkle events bypass React state: all three consumers (bubbles,
  // blocks, map) animate imperatively, and a ref-based bus can't drop
  // same-tick events the way batched setState would. null seen-map means
  // the first poll hasn't landed yet (it seeds silently, no sparkles).
  const seenClipsRef = useRef(null)
  const sparkleListenersRef = useRef(new Set())
  const sparkleTimersRef = useRef(new Set())

  const subscribeSparkles = useCallback((listener) => {
    sparkleListenersRef.current.add(listener)
    return () => sparkleListenersRef.current.delete(listener)
  }, [])

  const emitSparkle = useCallback((event) => {
    sparkleListenersRef.current.forEach((listener) => listener(event))
  }, [])

  const scheduleSparkle = useCallback(
    (event, fireAtMs) => {
      const timer = setTimeout(() => {
        sparkleTimersRef.current.delete(timer)
        emitSparkle(event)
      }, Math.max(0, fireAtMs - Date.now()))
      sparkleTimersRef.current.add(timer)
    },
    [emitSparkle]
  )

  useEffect(() => {
    const timers = sparkleTimersRef.current
    return () => timers.forEach(clearTimeout)
  }, [])

  const loadClips = useCallback(async () => {
    let data
    try {
      data = await api.recordingMetrics.getRecentClips()
    } catch {
      // Sparkles are decoration; a failed poll just skips a beat.
      return
    }
    const previous = seenClipsRef.current
    const next = new Map()
    data.clips.forEach((clip) => {
      const text = clipText(clip)
      const seen = previous ? previous.get(clip.source_id) : undefined
      // A clip that once had text keeps it even if a later (cached)
      // response briefly reorders around it.
      next.set(clip.source_id, {
        hasText: Boolean(text) || Boolean(seen && seen.hasText),
      })
      if (!previous) return
      const event = {
        id: clip.source_id,
        personaKey: clipPersonaKey(clip),
        timezoneId: clip.timezone_id,
        text,
      }
      if (!seen) {
        // Tape delay: replay at created_at + delay, preserving spacing.
        const fireAt = Date.parse(clip.created_at) + CLIP_SPARKLE_DELAY_MS
        if (Date.now() - fireAt > CLIP_SPARKLE_MAX_OVERDUE_MS) return
        scheduleSparkle({ ...event, type: 'create' }, fireAt)
      } else if (QUOTE_SPARKLE_ENABLED && !seen.hasText && text) {
        // No server timestamp for "text landed", so fire on detection —
        // it is already minutes behind the capture either way.
        emitSparkle({ ...event, type: 'quote' })
      }
    })
    // Clips absent from the response aged out of the server window;
    // dropping them keeps the seen-map bounded.
    seenClipsRef.current = next
  }, [scheduleSparkle, emitSparkle])

  useEffect(() => {
    loadClips()
    const interval = setInterval(loadClips, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [loadClips])

  // Fakes a clip event through the sparkle bus — a create sparkle now,
  // the quote sparkle 2s later — so the animations can be previewed
  // without waiting for a real clip. Targets a persona and timezone
  // that are actually live so all three effects are visible. Only
  // reachable from the SPARKLE_TEST_BUTTON header button.
  const testSparkle = useCallback(() => {
    const zones = (live ? live.timezones : []).filter(
      ({ timezone_id: id }) => id !== 'unknown'
    )
    const zone = zones.length
      ? zones[Math.floor(Math.random() * zones.length)]
      : null
    const cohorts = (live ? live.cohorts : []).filter(({ count }) => count > 0)
    const cohort = cohorts.length
      ? cohorts[Math.floor(Math.random() * cohorts.length)]
      : null
    const event = {
      id: `test-${Date.now()}`,
      personaKey: cohort ? clipPersonaKey(cohort) : PERSONAS[0].key,
      timezoneId: zone ? zone.timezone_id : 'unknown',
      text: null,
    }
    emitSparkle({ ...event, type: 'create' })
    setTimeout(() => {
      emitSparkle({
        ...event,
        type: 'quote',
        text: 'this is what a quote from a fresh clip looks like',
      })
    }, 2000)
  }, [live, emitSparkle])

  // Fullscreen the whole document, not this element: MUI tooltips and
  // the range-picker popover portal to document.body, which an
  // element-level fullscreen would cover up.
  useEffect(() => {
    const onChange = () => setFullscreen(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  // The scale factor that fits the fixed-width design to the screen,
  // whichever of width or height binds. offsetHeight reports layout
  // (pre-transform) pixels, so there's no feedback loop, and the
  // ResizeObserver catches natural-height changes (caption appearing,
  // data-driven wraps).
  const [wallboardScale, setWallboardScale] = useState(1)

  useEffect(() => {
    if (!fullscreen) return undefined
    const element = wallboardRef.current
    if (!element) return undefined
    const compute = () =>
      setWallboardScale(
        Math.min(
          (window.innerWidth - 96) / WALLBOARD_DESIGN_WIDTH,
          window.innerHeight / element.offsetHeight
        )
      )
    compute()
    const observer = new ResizeObserver(compute)
    observer.observe(element)
    window.addEventListener('resize', compute)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', compute)
    }
  }, [fullscreen])

  // Keep the display awake while in wallboard mode. The browser drops
  // the lock whenever the tab is hidden, so re-request on return to
  // visibility. Best-effort: if the API is missing or the request is
  // denied, the screen just sleeps on its normal schedule.
  useEffect(() => {
    if (!fullscreen || !navigator.wakeLock) return undefined
    let wakeLock = null
    let active = true
    const acquire = async () => {
      try {
        const lock = await navigator.wakeLock.request('screen')
        // The effect may have cleaned up while the request was in flight.
        if (active) wakeLock = lock
        else lock.release()
      } catch {
        // Denied (e.g. low battery) — nothing to do.
      }
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') acquire()
    }
    acquire()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      active = false
      document.removeEventListener('visibilitychange', onVisibility)
      if (wakeLock) wakeLock.release()
    }
  }, [fullscreen])

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen()
    else document.documentElement.requestFullscreen()
  }

  // Live and history load independently: a Cloud Monitoring outage must
  // not block the Mongo-backed live count (and vice versa). State is only
  // overwritten on success, so stale-but-valid data stays visible when a
  // background poll fails.
  const loadLive = useCallback(
    async ({ silent } = {}) => {
      try {
        setLive(await api.recordingMetrics.getLive())
      } catch (err) {
        // Background polls fail silently so a blip doesn't toast every 30s.
        if (!silent) toast({ message: err.message, severity: 'error' })
      }
      setLoading(false)
    },
    [toast]
  )

  const loadHistory = useCallback(async ({ silent } = {}) => {
    // Compare by identity when the response lands: if the range moved on
    // mid-flight (including to the mirrored Today preset), the stale
    // result must not overwrite the newer window's data.
    const requested = rangeRef.current
    if (!silent) setHistoryLoading(true)
    try {
      const data = await api.recordingMetrics.getHistory(
        resolveRange(requested)
      )
      if (rangeRef.current !== requested) return
      setHistory(data)
      setHistoryError(null)
    } catch (err) {
      if (rangeRef.current !== requested) return
      setHistoryError(err.message)
    }
    setHistoryLoading(false)
  }, [])

  // Recomputing startOf('day') on every call means a dashboard left
  // open rolls over to the new day's empty field at midnight on its own.
  const loadDay = useCallback(async () => {
    try {
      const data = await api.recordingMetrics.getHistory({
        start: dayjs().startOf('day').unix(),
      })
      setDay(data)
      setDayError(null)
    } catch (err) {
      setDayError(err.message)
    }
  }, [])

  useEffect(() => {
    loadLive()
    const interval = setInterval(
      () => loadLive({ silent: true }),
      POLL_INTERVAL_MS
    )
    return () => clearInterval(interval)
  }, [loadLive])

  useEffect(() => {
    loadDay()
    const interval = setInterval(loadDay, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [loadDay])

  const loadWeek = useCallback(async () => {
    try {
      setWeek(
        await api.recordingMetrics.getHistory({
          start: dayjs().subtract(7, 'day').unix(),
        })
      )
    } catch {
      // The week stats simply go stale; the panel body runs off `day`.
    }
  }, [])

  useEffect(() => {
    loadWeek()
    const interval = setInterval(loadWeek, WEEK_POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [loadWeek])

  // The "Today" preset (the default) asks for exactly what the pinned
  // day fetch already polls — start at midnight, open end — so the
  // timeline mirrors that data instead of issuing a duplicate query.
  const isTodayRange = range.key === 'today'

  useEffect(() => {
    if (!isTodayRange) return
    setHistory(day)
    setHistoryError(dayError)
    setHistoryLoading(false)
  }, [isTodayRange, day, dayError])

  // Refetch when the range changes (no page reload — the mounted chart
  // animates to the new window). Only live-edged ranges (presets, where
  // end is open) keep polling; fixed custom/zoom windows never change.
  useEffect(() => {
    if (isTodayRange) return undefined
    loadHistory()
    if (resolveRange(range).end !== null) return undefined
    const interval = setInterval(
      () => loadHistory({ silent: true }),
      POLL_INTERVAL_MS
    )
    return () => clearInterval(interval)
  }, [range, isTodayRange, loadHistory])

  // The two history charts are connected (shared tooltip group), so one
  // drag-select can echo a second identical datazoom from the sibling —
  // keep the same range object to avoid refetching twice.
  const handleZoom = useCallback((start, end) => {
    setRange((prev) =>
      prev.key === 'custom' && prev.start === start && prev.end === end
        ? prev
        : { key: 'custom', start, end }
    )
  }, [])

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 8 }}
      >
        <CircularProgress />
      </Box>
    )
  }

  const freshnessMinutes = live ? Math.round(live.freshness_seconds / 60) : null

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: fullscreen ? 'none' : 1600,
          px: 6,
          pb: 6,
        }}
      >
        {/* In wallboard mode the outer box reserves exactly one screen
            so the timeline stays below the fold; the inner wrapper is
            the fixed-width design that scales uniformly to fit. The
            wrapper keeps its unscaled layout footprint (transforms
            don't affect layout), so the outer box clips the overflow —
            the scaled visual always fits one screen by construction. */}
        <Box
          sx={fullscreen ? { height: '100vh', overflow: 'hidden' } : undefined}
        >
          <Box
            ref={wallboardRef}
            sx={{
              pt: 3,
              ...(fullscreen && {
                width: WALLBOARD_DESIGN_WIDTH,
                // Centers the fixed-width layout box even when it is
                // wider than the screen (mx auto resolves to 0 then).
                ml: `calc(50% - ${WALLBOARD_DESIGN_WIDTH / 2}px)`,
                pb: 2,
                transformOrigin: 'top center',
                transform: `scale(${wallboardScale})`,
              }),
            }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}
            >
              <Typography variant="h5" fontWeight={700}>
                Heartbeats
              </Typography>
              <Tooltip
                title="Live — refreshes every 30 seconds"
                placement="right"
              >
                <Box sx={{ display: 'inline-flex' }}>
                  <Pulse />
                </Box>
              </Tooltip>
              <Box sx={{ flex: 1 }} />
              {SPARKLE_TEST_BUTTON && (
                <Tooltip title="Test sparkle" placement="left">
                  <IconButton size="small" onClick={testSparkle}>
                    <AutoAwesomeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip
                title={fullscreen ? 'Exit full screen' : 'Full screen'}
                placement="left"
              >
                <IconButton size="small" onClick={toggleFullscreen}>
                  {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Users with their mic on right now, and how that count has moved
              over time. For long-term, detailed metrics see the{' '}
              <Link
                href="https://metrics-dashboard-du22ae4u2a-uc.a.run.app/"
                target="_blank"
                rel="noopener"
              >
                executive dashboard
              </Link>
              .
            </Typography>
            {freshnessMinutes !== null && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 2, display: 'block' }}
              >
                &quot;On&quot; means a recording heartbeat within the last{' '}
                {freshnessMinutes} minutes (clients beat every ~5 min) — clean
                stops drop out immediately, crashed apps decay over that window.
              </Typography>
            )}

            {/* Columns stretch so the map panel's bottom sits flush with
                the day-blocks panel. */}
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                alignItems: 'stretch',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 480 }}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 3,
                    mb: 3,
                    flexWrap: 'wrap',
                    alignItems: 'stretch',
                  }}
                >
                  <Paper
                    variant="outlined"
                    sx={{ p: 3, borderRadius: 2, width: 520, flexShrink: 0 }}
                  >
                    <MicTimeBucket day={day} week={week} />
                  </Paper>

                  {live && (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        flex: 1,
                        minWidth: 340,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight={700}>
                          On right now
                        </Typography>
                        <Tooltip
                          title={gathered ? 'Set adrift' : 'Gather to center'}
                          placement="left"
                        >
                          <IconButton
                            size="small"
                            color={gathered ? 'default' : 'primary'}
                            onClick={() => setGathered((value) => !value)}
                          >
                            {gathered ? (
                              <AllOutIcon fontSize="small" />
                            ) : (
                              <FilterCenterFocusIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <BubbleField
                        cohorts={live.cohorts}
                        mode={gathered ? 'center' : 'drift'}
                        subscribeSparkles={subscribeSparkles}
                      />
                    </Paper>
                  )}
                </Box>

                <DayBlocksPanel
                  day={day}
                  dayError={dayError}
                  subscribeSparkles={subscribeSparkles}
                />
              </Box>

              <Box
                sx={{
                  width: 280,
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                  {live ? (
                    <>
                      <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            // Baseline, not center: centering uses the
                            // overline's full line box (with descender
                            // space the caps never use) and floats the dot
                            // high. Bottom-of-dot on the baseline puts its
                            // center at the cap-height midpoint.
                            alignItems: 'baseline',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          {/* Recording indicator, in the mic-on orange of
                              the iOS live activity / dynamic island. */}
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: '#FF9900',
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="overline"
                            color="text.secondary"
                            sx={{ letterSpacing: 2 }}
                          >
                            Mics on now
                          </Typography>
                        </Box>
                        <Typography
                          variant="h1"
                          fontWeight={800}
                          lineHeight={1}
                          sx={{ fontSize: 84 }}
                        >
                          {live.total}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 3 }}>
                        <CohortSankey cohorts={live.cohorts} />
                      </Box>
                      <Breakdown cohorts={live.cohorts} />
                    </>
                  ) : (
                    <Typography color="error">
                      Failed to load the live count.
                    </Typography>
                  )}
                </Paper>

                {/* Click-through preview of the full-size map below the
                    timelines. */}
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    py: 2,
                    borderRadius: 2,
                    cursor: 'pointer',
                    // Fills the column down to the day-blocks baseline;
                    // the map centers in whatever height that leaves.
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  onClick={() =>
                    worldMapRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    })
                  }
                >
                  <TimezoneMap
                    timezones={live ? live.timezones : null}
                    preview
                    subscribeSparkles={subscribeSparkles}
                  />
                </Paper>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Everything above is live, pinned to today; below is the
            adjustable history section. */}
        <Divider sx={{ my: 4 }} />

        <TimelinePanel
          history={history}
          historyError={historyError}
          historyLoading={historyLoading}
          range={range}
          setRange={setRange}
          onZoom={handleZoom}
        />

        <Paper
          ref={worldMapRef}
          variant="outlined"
          sx={{ p: 3, mt: 3, borderRadius: 2, scrollMarginTop: 16 }}
        >
          <TimezoneMap
            timezones={live ? live.timezones : null}
            subscribeSparkles={subscribeSparkles}
          />
        </Paper>

        <Paper
          variant="outlined"
          sx={{ p: 3, mt: 3, borderRadius: 2, maxWidth: 360 }}
        >
          <SessionAverages day={day} week={week} />
        </Paper>
      </Box>
    </Box>
  )
}

const DayBlocksPanel = ({ day, dayError, subscribeSparkles }) => (
  <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: 2,
        mb: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          The day so far
        </Typography>
        <Tooltip
          title="Each column stacks the users on at that 15-minute interval's busiest moment — midnight to midnight, filling in live."
          placement="right"
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 16, color: 'text.secondary', cursor: 'default' }}
          />
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        {PERSONAS.map(({ key, color, legend }) => (
          <Tooltip key={key} title={legend} placement="top">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: 0.5,
                  bgcolor: color,
                }}
              />
              <Typography variant="caption">{key}</Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>

    {day && day.series.length > 0 ? (
      <>
        <DayBlocks
          series={day.series}
          dayStart={day.start}
          subscribeSparkles={subscribeSparkles}
        />
        {dayError && (
          <Typography variant="caption" color="error" display="block">
            Blocks may be stale — the last refresh failed: {dayError}
          </Typography>
        )}
      </>
    ) : dayError ? (
      <Typography
        variant="body2"
        color="error"
        sx={{ py: 8, textAlign: 'center' }}
      >
        Couldn&apos;t load the day: {dayError}
      </Typography>
    ) : day ? (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ py: 8, textAlign: 'center' }}
      >
        Nothing to stack yet — no mic-on data recorded today.
      </Typography>
    ) : (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={20} />
      </Box>
    )}
  </Paper>
)

const TimelinePanel = ({
  history,
  historyError,
  historyLoading,
  range,
  setRange,
  onZoom,
}) => (
  <Paper
    variant="outlined"
    sx={{ p: 3, borderRadius: 2, position: 'relative', overflow: 'hidden' }}
  >
    {historyLoading && (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          overflow: 'hidden',
          pointerEvents: 'none',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: '35%',
            background: (theme) =>
              `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
            animation: 'panel-loading-sweep 1s ease-in-out infinite',
          },
          '@keyframes panel-loading-sweep': {
            from: { left: '-35%' },
            to: { left: '100%' },
          },
        }}
      />
    )}
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2,
        mb: 2,
      }}
    >
      <Typography variant="subtitle1" fontWeight={700}>
        Online over time
      </Typography>
      <RangePicker range={range} onChange={setRange} />
    </Box>

    {history && history.series.length > 0 ? (
      <>
        <Timeline
          series={history.series}
          rangeStart={history.start}
          rangeEnd={history.end}
          onZoom={onZoom}
        />
        <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
          Cumulative mic-on time
        </Typography>
        <CumulativeTimeline history={history} onZoom={onZoom} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}
        >
          Resolution: {history.step_seconds}s
        </Typography>
        {historyError && (
          <Typography variant="caption" color="error" display="block">
            Timeline may be stale — the last refresh failed: {historyError}
          </Typography>
        )}
      </>
    ) : historyError ? (
      <Typography
        variant="body2"
        color="error"
        sx={{ py: 8, textAlign: 'center' }}
      >
        Couldn&apos;t load the timeline: {historyError}
      </Typography>
    ) : history ? (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ py: 8, textAlign: 'center' }}
      >
        No data points in this window — the range may predate metric collection,
        or collection isn&apos;t enabled in this environment (the live count
        above still works).
      </Typography>
    ) : (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={20} />
      </Box>
    )}
  </Paper>
)

export default Heartbeats
